import { dirname } from 'node:path';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { pathExists, readJson } from '@wix/cli-fs';
import { supportedServicePlugin } from '@wix/cli-app-definitions';
import type { SupportedServicePlugin } from '@wix/cli-app-definitions';
import type { z } from 'zod';
import { replaceExtension } from '../path.js';

function FolderNameToServicePluginType(folderName: string) {
  return folderName.replaceAll('-', '_').toUpperCase();
}

export function ServicePluginTypeToFolderName(
  servicePluginType: SupportedServicePlugin
) {
  return servicePluginType.toLowerCase().replaceAll('_', '-');
}

export function deriveServicePluginType(
  relativePath: string
): SupportedServicePlugin {
  const folderName = dirname(relativePath).split('/').at(-2);

  if (!folderName) {
    throw new CliError({
      code: CliErrorCode.InvalidServicePluginFolder({
        relativePath,
      }),
      cause: null,
    });
  }

  const transformedFolderName = FolderNameToServicePluginType(folderName);
  const parsedPluginType = supportedServicePlugin.safeParse(
    transformedFolderName
  );

  if (parsedPluginType.success) {
    return parsedPluginType.data;
  }

  throw new CliError({
    code: CliErrorCode.UnknownServicePlugin({
      name: folderName,
    }),
    cause: parsedPluginType.error,
  });
}

export async function loadServicePluginConfig<T>(
  servicePluginPath: string,
  servicePluginSchema: z.ZodType<T>
) {
  const configPath = replaceExtension(servicePluginPath, 'json');
  const isConfigExist = await pathExists(configPath);

  if (!isConfigExist) {
    throw new CliError({
      code: CliErrorCode.ServicePluginConfigNotFound({ configPath }),
      cause: null,
    });
  }

  const config = await readJson(configPath);
  const parsedConfig = servicePluginSchema.safeParse(config);

  if (parsedConfig.success) {
    return parsedConfig.data;
  }

  throw new CliError({
    code: CliErrorCode.InvalidConfigSchemaError({
      configPath,
      zodError: parsedConfig.error,
    }),
    cause: parsedConfig.error,
  });
}
